
import React, { useState, useEffect, useRef } from 'react';
import './ProductDetail.css';
import { useApp } from '../../context/AppContext';
import OfferModal from '../../components/OfferModal/OfferModal';

const ProductDetail: React.FC = () => {
  const { selectedProduct, toggleWishlist, currentUser, products, setActiveTab, makeOffer, getProductOffers } = useApp();
  const [imageIndex, setImageIndex] = useState(0);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const actionButtonsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (actionButtonsRef.current) {
        const rect = actionButtonsRef.current.getBoundingClientRect();
        // Show sticky CTA when action buttons are scrolled out of view
        setShowStickyCta(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!selectedProduct) return null;

  const product = selectedProduct;
  const isWishlisted = currentUser.wishlist.includes(product.id);
  const relatedProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleMakeOffer = () => {
    setShowOfferModal(true);
  };

  const handleSubmitOffer = (amount: number, message: string) => {
    // Submit offer through the offer system
    makeOffer(product.id, amount, message || undefined);
    setShowOfferModal(false);
  };

  const offers = getProductOffers(product.id);
  const topOffer = product.topOffer || (offers.length > 0 ? Math.max(...offers.map(o => o.amount)) : null);

  return (
    <div className="product-detail">
      <div className="detail-images">
        <div className="main-image">
          <img src={product.images[imageIndex]} alt={product.name} />
        </div>
        {product.images.length > 1 && (
          <div className="image-thumbs">
            {product.images.map((img, i) => (
              <button
                key={i}
                className={`thumb ${i === imageIndex ? 'active' : ''}`}
                onClick={() => setImageIndex(i)}
              >
                <img src={img} alt={`View ${i + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="detail-info">
        <div className="info-header">
          <h1 className="product-title">{product.name}</h1>
          <button 
            className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
            onClick={() => toggleWishlist(product.id)}
          >
            <svg viewBox="0 0 24 24" fill={isWishlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
          </button>
        </div>

        <div className="rating-row">
          <div className="stars">
            {[1,2,3,4,5].map(i => (
              <svg key={i} viewBox="0 0 24 24" fill={i <= Math.round(product.rating) ? 'currentColor' : 'none'} stroke="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            ))}
          </div>
          <span className="rating-text">{product.rating} ({product.reviews} reviews)</span>
        </div>

        <div className="price-row">
          <span className="unique-item-badge">1 of 1 - Personally Owned</span>
          <div className="starting-bid-row">
            <span className="starting-bid-label">Starting bid</span>
            <span className="starting-bid-price">${(product.salePrice || product.price).toFixed(0)}</span>
          </div>
          {topOffer && (
            <div className="top-offer-badge">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              Top Offer: ${topOffer}
            </div>
          )}
          {offers.length > 0 && (
            <span className="offer-count">{offers.length} offer{offers.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        <p className="description">{product.description}</p>

        <div className="item-details">
          <div className="detail-item">
            <span className="detail-icon">✨</span>
            <span>Authentic item from Jalen's personal collection</span>
          </div>
          <div className="detail-item">
            <span className="detail-icon">📦</span>
            <span>Ships with certificate of authenticity</span>
          </div>
          <div className="detail-item">
            <span className="detail-icon">💬</span>
            <span>Message to discuss price and details</span>
          </div>
        </div>


        {relatedProducts.length > 0 && (
          <div className="related-section">
            <h3>You May Also Like</h3>
            <div className="related-scroll">
              {relatedProducts.map(p => (
                <div key={p.id} className="related-item">
                  <img src={p.images[0]} alt={p.name} />
                  <span className="related-name">{p.name}</span>
                  <span className="related-price">Make an Offer</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky CTA Bar */}
      <div className={`sticky-cta-bar ${showStickyCta ? 'visible' : ''}`}>
        <div className="sticky-cta-content">
          <div className="sticky-cta-info">
            <span className="sticky-cta-name">{product.name}</span>
            <span className="sticky-cta-badge">1 of 1 - Unique</span>
          </div>
          <button className="sticky-cta-btn" onClick={handleMakeOffer}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Make an Offer
          </button>
        </div>
      </div>

      {/* Offer Modal */}
      {showOfferModal && (
        <OfferModal
          product={{
            id: product.id,
            name: product.name,
            images: product.images,
            price: product.price,
            salePrice: product.salePrice,
            topOffer: topOffer,
          }}
          onClose={() => setShowOfferModal(false)}
          onSubmit={handleSubmitOffer}
        />
      )}
    </div>
  );
};

export default ProductDetail;
